'use client';

import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useAppColors } from '@/styles/colors';
import { RiskMomentCard } from './RiskMomentCard';
import { getCorporateEventColor, getCorporateEventDisplayLabel } from '@/libs/corporate-event-chart';

type PricePoint = {
  date: string;
  close: number;
};

type BenchmarkLine = {
  key: string;
  label: string;
  series: Array<{ date: string; value: number }>;
};

type TickerPriceHistoryChartProps = {
  tickerLabel: string;
  priceSeries: PricePoint[];
  corporateEvents: Array<{
    date: string;
    eventType: string;
    label: string | null;
    splitMultiplier: number | null;
    subscriptionPercentage: number | null;
  }>;
  benchmarks?: BenchmarkLine[];
  onOpenBenchmarks?: () => void;
};

type RangeId = '1M' | '3M' | '6M' | '1A' | '5A' | 'TUDO';

const DAY_MS = 24 * 60 * 60 * 1000;

const RANGE_CONFIG: Array<{ id: RangeId; label: string; days: number | null }> = [
  { id: '1M', label: '1M', days: 30 },
  { id: '3M', label: '3M', days: 90 },
  { id: '6M', label: '6M', days: 180 },
  { id: '1A', label: '1A', days: 365 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const DATE_FORMATTER = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const toDateKey = (value: string) => value.slice(0, 10);

const formatPct = (value: number) =>
  `${value > 0 ? '+' : ''}${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)}%`;

const mapValueToY = (value: number, minValue: number, maxValue: number, chartTop: number, chartHeight: number) => {
  if (maxValue === minValue) return chartTop + chartHeight / 2;
  const ratio = (value - minValue) / (maxValue - minValue);
  return chartTop + chartHeight - ratio * chartHeight;
};

export function TickerPriceHistoryChart({
  tickerLabel,
  priceSeries,
  corporateEvents,
  benchmarks = [],
  onOpenBenchmarks,
}: TickerPriceHistoryChartProps) {
  const colors = useAppColors();
  const [range, setRange] = useState<RangeId>('6M');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const BENCHMARK_COLORS = colors.modules.priceHistorySeries;
  const PRICE_COLOR = colors.modules.simulation.price;
  const rangeColors = colors.actions.range;

  const filteredSeries = useMemo(() => {
    if (priceSeries.length === 0) return [];

    const selected = RANGE_CONFIG.find((item) => item.id === range);
    if (!selected || selected.days === null) return priceSeries;

    const lastDate = new Date(priceSeries[priceSeries.length - 1].date).getTime();
    const threshold = lastDate - selected.days * DAY_MS;
    const next = priceSeries.filter((item) => {
      const ts = new Date(item.date).getTime();
      return Number.isFinite(ts) && ts >= threshold;
    });
    return next.length > 1 ? next : priceSeries;
  }, [priceSeries, range]);

  const filteredBenchmarks = useMemo(() => {
    if (benchmarks.length === 0) return [] as BenchmarkLine[];
    const selected = RANGE_CONFIG.find((item) => item.id === range);
    if (!selected || selected.days === null) return benchmarks;
    const lastDate = new Date(filteredSeries[filteredSeries.length - 1]?.date ?? '').getTime();
    if (!Number.isFinite(lastDate)) return benchmarks;
    const threshold = lastDate - selected.days * DAY_MS;
    return benchmarks
      .map((line) => {
        const next = line.series.filter((item) => {
          const ts = new Date(`${item.date}T00:00:00Z`).getTime();
          return Number.isFinite(ts) && ts >= threshold;
        });
        return {
          ...line,
          series: next.length > 1 ? next : line.series,
        };
      })
      .filter((line) => line.series.length > 0);
  }, [benchmarks, range, filteredSeries]);
  const filteredCorporateEvents = useMemo(() => {
    if (filteredSeries.length === 0) return [];
    const startDate = toDateKey(filteredSeries[0].date);
    const endDate = toDateKey(filteredSeries[filteredSeries.length - 1].date);
    return corporateEvents
      .filter((event) => event.date >= startDate && event.date <= endDate)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [corporateEvents, filteredSeries]);

  const stats = useMemo(() => {
    const first = filteredSeries[0];
    const last = filteredSeries[filteredSeries.length - 1];
    if (!first || !last) return null;

    let min = filteredSeries[0].close;
    let max = filteredSeries[0].close;
    for (const point of filteredSeries) {
      min = Math.min(min, point.close);
      max = Math.max(max, point.close);
    }

    const delta = first.close === 0 ? 0 : ((last.close - first.close) / first.close) * 100;
    return {
      first,
      last,
      min,
      max,
      delta,
    };
  }, [filteredSeries]);

  if (!stats) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Não foi possível renderizar o gráfico de preços.
      </Paper>
    );
  }

  const width = 1000;
  const height = 360;
  const chartLeft = 60;
  const chartRight = width - 24;
  const chartTop = 24;
  const chartBottom = height - 48;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;
  const plotSeries = filteredSeries;

  const mapTsToX = (ts: number, minTs: number, maxTs: number) => {
    if (maxTs === minTs) return chartLeft + chartWidth / 2;
    return chartLeft + ((ts - minTs) / (maxTs - minTs)) * chartWidth;
  };

  const startTs = new Date(plotSeries[0].date).getTime();
  const endTs = new Date(plotSeries[plotSeries.length - 1].date).getTime();

  const normalizedBenchmarks = filteredBenchmarks.map((line, index) => {
    if (line.series.length === 0 || line.series[0].value <= 0) {
      return {
        ...line,
        color: BENCHMARK_COLORS[index % BENCHMARK_COLORS.length],
        normalizedSeries: [] as Array<{ date: string; value: number }>,
      };
    }
    const factor = plotSeries[0].close / line.series[0].value;
    return {
      ...line,
      color: BENCHMARK_COLORS[index % BENCHMARK_COLORS.length],
      normalizedSeries: line.series.map((point) => ({
        date: point.date,
        value: point.value * factor,
      })),
    };
  });

  let chartMin = stats.min;
  let chartMax = stats.max;
  for (const line of normalizedBenchmarks) {
    for (const point of line.normalizedSeries) {
      chartMin = Math.min(chartMin, point.value);
      chartMax = Math.max(chartMax, point.value);
    }
  }
  const yHeadroom = Math.max(chartMax - chartMin, Math.abs(chartMax), 1) * 0.08;
  chartMax += yHeadroom;

  const points = plotSeries.map((point, index) => {
    const x =
      plotSeries.length === 1 ? chartLeft + chartWidth / 2 : chartLeft + (index / (plotSeries.length - 1)) * chartWidth;
    const y = mapValueToY(point.close, chartMin, chartMax, chartTop, chartHeight);
    return { x, y };
  });
  const linePoints = points.map((point) => `${point.x},${point.y}`).join(' ');
  const areaPoints = `${chartLeft},${chartBottom} ${linePoints} ${chartRight},${chartBottom}`;
  const yTicks = 5;

  const benchmarkDisplay = normalizedBenchmarks.map((line) => {
    const benchmarkPoints = line.normalizedSeries.map((point) => {
      const ts = new Date(`${point.date}T00:00:00Z`).getTime();
      const x = mapTsToX(ts, startTs, endTs);
      const y = mapValueToY(point.value, chartMin, chartMax, chartTop, chartHeight);
      return { x, y };
    });
    const linePointsText = benchmarkPoints.map((point) => `${point.x},${point.y}`).join(' ');

    const carryMap = new Map<string, number>();
    let idx = 0;
    let last: number | null = null;
    for (const point of plotSeries) {
      const pointDate = toDateKey(point.date);
      while (idx < line.series.length && line.series[idx].date <= pointDate) {
        last = line.series[idx].value;
        idx += 1;
      }
      if (last !== null) carryMap.set(pointDate, last);
    }

    return {
      key: line.key,
      label: line.label,
      color: line.color,
      linePointsText,
      carryMap,
      hasPoints: benchmarkPoints.length > 1,
    };
  });

  const hoveredPoint =
    hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < plotSeries.length
      ? {
          ...points[hoveredIndex],
          data: plotSeries[hoveredIndex],
        }
      : null;

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Paper variant='outlined' sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
        >
          <Typography variant='h6' fontWeight={700}>
            Cotação (ajustado) de {tickerLabel}
          </Typography>
          <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
            {onOpenBenchmarks ? (
              <Button
                size='small'
                variant='outlined'
                onClick={onOpenBenchmarks}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: 110,
                  px: 1.5,
                  borderRadius: 1.5,
                  mr: { md: 0.5 },
                  color: 'text.primary',
                  borderColor: 'text.secondary',
                  backgroundColor: 'background.paper',
                  transition: 'all 120ms ease',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'text.primary',
                  },
                  '&:active': {
                    transform: 'translateY(1px)',
                  },
                }}
              >
                Comparar ▾
              </Button>
            ) : null}
            {RANGE_CONFIG.map((item) => (
              <Button
                key={item.id}
                size='small'
                variant={range === item.id ? 'contained' : 'outlined'}
                onClick={() => setRange(item.id)}
                sx={
                  range === item.id
                    ? {
                        bgcolor: rangeColors.selectedBg,
                        borderColor: rangeColors.selectedBorder,
                        color: rangeColors.selectedText,
                        '&:hover': {
                          bgcolor: rangeColors.selectedHoverBg,
                          borderColor: rangeColors.selectedHoverBorder,
                        },
                      }
                    : {
                        color: rangeColors.unselectedText,
                        borderColor: rangeColors.unselectedBorder,
                        '&:hover': {
                          color: rangeColors.unselectedHoverText,
                          borderColor: rangeColors.unselectedHoverBorder,
                          backgroundColor: rangeColors.unselectedHoverBg,
                        },
                      }
                }
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ mt: 2, mb: 1 }}>
          <Metric label='Valor atual' value={BRL_FORMATTER.format(stats.last.close)} />
          <Metric label='Mín. período' value={BRL_FORMATTER.format(stats.min)} />
          <Metric label='Máx. período' value={BRL_FORMATTER.format(stats.max)} />
          <Metric label='Variação período' value={formatPct(stats.delta)} positive={stats.delta >= 0} />
        </Stack>

        <Stack direction='row' spacing={1.25} flexWrap='wrap' useFlexGap sx={{ mb: 1 }}>
          <LegendItem label='Cotação (ajustado)' color={PRICE_COLOR} />
          {benchmarkDisplay.map((line) => (
            <LegendItem
              key={line.key}
              label={line.hasPoints ? line.label : `${line.label} (sem dados)`}
              color={line.color}
              dashed
              dimmed={!line.hasPoints}
            />
          ))}
          <LegendItem
            label={`Eventos (${filteredCorporateEvents.length})`}
            color={colors.modules.simulation.eventMarker}
          />
        </Stack>

        <Box sx={{ width: '100%', mt: 1 }}>
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width='100%'
            role='img'
            onMouseLeave={() => setHoveredIndex(null)}
            onMouseMove={(event) => {
              if (plotSeries.length === 0) return;
              const rect = event.currentTarget.getBoundingClientRect();
              if (rect.width <= 0 || chartWidth <= 0) return;
              const relativeX = ((event.clientX - rect.left) / rect.width) * width;
              const clampedX = Math.max(chartLeft, Math.min(chartRight, relativeX));
              const ratio = (clampedX - chartLeft) / chartWidth;
              const index = Math.round(ratio * (plotSeries.length - 1));
              const safeIndex = Math.max(0, Math.min(plotSeries.length - 1, index));
              setHoveredIndex(safeIndex);
            }}
          >
            <title>{`Gráfico de cotação diária ajustada de ${tickerLabel}`}</title>
            <rect
              x={chartLeft}
              y={chartTop}
              width={chartWidth}
              height={chartHeight}
              fill='transparent'
              stroke='hsl(var(--border))'
            />

            {Array.from({ length: yTicks }).map((_, idx) => {
              const ratio = idx / (yTicks - 1);
              const y = chartTop + ratio * chartHeight;
              const value = chartMax - ratio * (chartMax - chartMin);
              return (
                <g key={idx}>
                  <line
                    x1={chartLeft}
                    y1={y}
                    x2={chartRight}
                    y2={y}
                    stroke='hsl(var(--border))'
                    strokeDasharray='4 4'
                  />
                  <text x={chartLeft - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                    {BRL_FORMATTER.format(value)}
                  </text>
                </g>
              );
            })}

            <polygon points={areaPoints} fill={`${PRICE_COLOR}33`} />
            <polyline points={linePoints} fill='none' stroke={PRICE_COLOR} strokeWidth='2' />

            {benchmarkDisplay.map((line) =>
              line.hasPoints ? (
                <polyline
                  key={line.key}
                  points={line.linePointsText}
                  fill='none'
                  stroke={line.color}
                  strokeWidth='2'
                  strokeDasharray='5 4'
                />
              ) : null,
            )}
            {filteredCorporateEvents.map((event, index) => {
              const x = mapTsToX(new Date(`${event.date}T00:00:00Z`).getTime(), startTs, endTs);
              const color = getCorporateEventColor(event);
              return (
                <g key={`${event.date}-${event.eventType}-${index}`}>
                  <line
                    x1={x}
                    y1={chartTop}
                    x2={x}
                    y2={chartBottom}
                    stroke={color}
                    strokeWidth='1.2'
                    strokeDasharray='3 3'
                    opacity='0.75'
                  />
                  <rect x={x - 2} y={chartTop + 2} width='4' height='8' rx='1' fill={color}>
                    <title>
                      {`${getCorporateEventDisplayLabel(event)} em ${DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))}`}
                    </title>
                  </rect>
                </g>
              );
            })}

            {points.map((point, idx) => (
              <circle key={idx} cx={point.x} cy={point.y} r='2.2' fill={PRICE_COLOR} stroke='hsl(var(--background))' />
            ))}

            <rect
              x={chartLeft}
              y={chartTop}
              width={chartWidth}
              height={chartHeight}
              fill='transparent'
              pointerEvents='all'
            />

            {hoveredPoint ? (
              <>
                <line
                  x1={hoveredPoint.x}
                  y1={chartTop}
                  x2={hoveredPoint.x}
                  y2={chartBottom}
                  stroke='hsl(var(--muted-foreground) / 0.6)'
                  strokeDasharray='4 4'
                />
                <circle
                  cx={hoveredPoint.x}
                  cy={hoveredPoint.y}
                  r='4'
                  fill={PRICE_COLOR}
                  stroke={colors.semantic.white}
                  strokeWidth='2'
                />
                <g
                  transform={`translate(${Math.min(
                    chartRight - 220,
                    Math.max(chartLeft + 8, hoveredPoint.x + 10),
                  )}, ${chartTop + 8})`}
                >
                  <rect
                    width='210'
                    height={40 + benchmarkDisplay.length * 14}
                    rx='8'
                    fill='hsl(var(--card))'
                    stroke='hsl(var(--border))'
                  />
                  <text x='10' y='18' fontSize='12' fill='hsl(var(--muted-foreground))'>
                    {DATE_FORMATTER.format(new Date(hoveredPoint.data.date))}
                  </text>
                  <text x='10' y='34' fontSize='13' fontWeight='700' fill={PRICE_COLOR}>
                    {`Cotação (ajustado): ${BRL_FORMATTER.format(hoveredPoint.data.close)}`}
                  </text>
                  {benchmarkDisplay.map((line, idx) => {
                    const value = line.carryMap.get(toDateKey(hoveredPoint.data.date));
                    return (
                      <text key={line.key} x='10' y={48 + idx * 14} fontSize='12' fill={line.color}>
                        {`${line.label}: ${value === undefined ? '-' : NUMBER_FORMATTER.format(value)}`}
                      </text>
                    );
                  })}
                </g>
              </>
            ) : null}

            <text x={chartLeft} y={height - 16} fontSize='12' fill='hsl(var(--muted-foreground))'>
              {DATE_FORMATTER.format(new Date(stats.first.date))}
            </text>
            <text x={chartRight} y={height - 16} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
              {DATE_FORMATTER.format(new Date(stats.last.date))}
            </text>
          </svg>
        </Box>
        {filteredCorporateEvents.length > 0 ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 700 }}>
              Eventos corporativos no período
            </Typography>
            <Stack direction='row' spacing={0.75} useFlexGap flexWrap='wrap' sx={{ mt: 0.5 }}>
              {filteredCorporateEvents.slice(0, 8).map((event, index) => (
                <Stack
                  key={`${event.date}-${event.eventType}-${index}`}
                  direction='row'
                  spacing={0.5}
                  alignItems='center'
                  sx={{
                    px: 0.75,
                    py: 0.35,
                    borderRadius: 1,
                    backgroundColor: 'hsl(var(--muted) / 0.35)',
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: getCorporateEventColor(event),
                    }}
                  />
                  <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {DATE_FORMATTER.format(new Date(`${event.date}T00:00:00Z`))} ·{' '}
                    {getCorporateEventDisplayLabel(event)}
                  </Typography>
                </Stack>
              ))}
              {filteredCorporateEvents.length > 8 ? (
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  +{filteredCorporateEvents.length - 8} eventos
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}
      </Paper>
      <RiskMomentCard priceSeries={filteredSeries} />
    </Stack>
  );
}

function Metric({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography
        variant='h6'
        fontWeight={700}
        color={positive === undefined ? 'text.primary' : positive ? 'success.main' : 'error.main'}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function LegendItem({
  label,
  color,
  dashed = false,
  dimmed = false,
}: {
  label: string;
  color: string;
  dashed?: boolean;
  dimmed?: boolean;
}) {
  return (
    <Stack direction='row' spacing={0.75} alignItems='center' sx={{ opacity: dimmed ? 0.6 : 1 }}>
      <Box
        sx={{
          width: 16,
          height: 0,
          borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}`,
          borderRadius: 999,
        }}
      />
      <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  );
}
